// Utils
import { createInertiaScrollDetector } from '../../../../../../canvas/zoom/inertiaScrollDetector';
import {
    createWheelModeTracker,
    getWheelZoomDeltaY,
    isPinchStyleZoomWheel,
    isWheelZoomScroll,
    WHEEL_ZOOM_MODES,
} from '../../../../../../canvas/zoom/wheelZoomInteractionUtils';

// Types
import { Point } from '../../../../../../../common/maths/geometry/pointTypes';
import { PlatformDetails } from '../../../../../../../common/platform/platformTypes';

export type ImageModalWheelInteractionHandlers = {
    getViewportCentrePoint: () => Point | null;
    getViewportPoint: (clientPoint: Point) => Point | null;
    isZoomed: boolean;
    panBy: (panDelta: Point) => void;
    recordZoomAnchorPoint: (anchorPoint: Point) => void;
    zoomWheelToPoint: (anchorPoint: Point, wheelDeltaY: number) => void;
};

export type ImageModalWheelInteractionState = {
    wheelModeTracker: ReturnType<typeof createWheelModeTracker>;
    inertiaScrollDetector: ReturnType<typeof createInertiaScrollDetector>;
    postZoomWheelScrollGuard: ReturnType<typeof createPostZoomWheelScrollGuard>;
};

/** How long unmodified wheel events stay attributed to the zoom gesture after its last modifier-zoom wheel event. */
const POST_ZOOM_WHEEL_SCROLL_IGNORE_WINDOW_MS = 100;

/**
 * Tracks the last zoom wheel event so the gesture's remaining wheel stream isn't treated as a
 * pan when the modifier key is released mid-gesture.
 *
 * Only zoom wheel events extend the window; ignored scroll events do not, so a deliberate
 * zoom-then-pan motion is delayed by at most the window duration.
 */
export const createPostZoomWheelScrollGuard = () => {
    let lastZoomWheelTimeStamp: number | null = null;

    return {
        recordZoomWheelEvent: (event: Pick<WheelEvent, 'timeStamp'>) => {
            lastZoomWheelTimeStamp = event.timeStamp;
        },
        shouldIgnoreScrollWheelEvent: (event: Pick<WheelEvent, 'timeStamp'>): boolean =>
            lastZoomWheelTimeStamp !== null &&
            event.timeStamp - lastZoomWheelTimeStamp <= POST_ZOOM_WHEEL_SCROLL_IGNORE_WINDOW_MS,
    };
};

export type ImageModalWheelInteractionOptions = {
    platformDetails: PlatformDetails;
};

/**
 * Handles regular wheel scrolling in the image modal, panning only when the image is zoomed.
 */
const processImageModalPanWheelEvent = (
    event: WheelEvent,
    state: ImageModalWheelInteractionState,
    handlers: ImageModalWheelInteractionHandlers,
    isInertiaScroll: boolean,
): void => {
    // The modifier was released mid-gesture — the remaining wheel stream still belongs to the zoom.
    if (state.postZoomWheelScrollGuard.shouldIgnoreScrollWheelEvent(event)) return;

    // Leftover inertia from zoom after modifier release — don't pan.
    if (state.wheelModeTracker.shouldIgnoreScrollInertia(isInertiaScroll)) {
        state.wheelModeTracker.setMode(WHEEL_ZOOM_MODES.NONE);
        state.wheelModeTracker.scheduleReset();
        return;
    }

    state.wheelModeTracker.setMode(WHEEL_ZOOM_MODES.SCROLL);
    state.wheelModeTracker.scheduleReset();

    if (handlers.isZoomed) {
        handlers.panBy({ x: -event.deltaX, y: -event.deltaY });
    }
};

/**
 * Handles modifier or pinch-style wheel zooming in the image modal.
 */
const processImageModalZoomWheelEvent = (
    event: WheelEvent,
    state: ImageModalWheelInteractionState,
    handlers: ImageModalWheelInteractionHandlers,
    { platformDetails }: ImageModalWheelInteractionOptions,
    isInertiaScroll: boolean,
): void => {
    if (state.wheelModeTracker.shouldIgnoreZoomInertia(isInertiaScroll)) return;

    // Zoom in tracks the cursor; zoom out re-centres via the viewport centre.
    const isZoomingOut = event.deltaY > 0;
    const anchorPoint = isZoomingOut
        ? handlers.getViewportCentrePoint()
        : handlers.getViewportPoint({ x: event.clientX, y: event.clientY });

    if (!anchorPoint) return;

    handlers.recordZoomAnchorPoint(anchorPoint);

    state.wheelModeTracker.setMode(WHEEL_ZOOM_MODES.ZOOM);
    state.wheelModeTracker.scheduleReset();
    state.postZoomWheelScrollGuard.recordZoomWheelEvent(event);

    handlers.zoomWheelToPoint(anchorPoint, getWheelZoomDeltaY(event, { platformDetails }));
};

/**
 * Handles wheel gestures for the image modal: scroll-to-pan when zoomed, modifier/pinch zoom otherwise.
 */
export const processImageModalWheelEvent = (
    event: WheelEvent,
    state: ImageModalWheelInteractionState,
    handlers: ImageModalWheelInteractionHandlers,
    { platformDetails }: ImageModalWheelInteractionOptions,
): void => {
    const isTrackpadPinchStyleWheel = isPinchStyleZoomWheel(event, { platformDetails });

    state.inertiaScrollDetector.addWheelEvent(event, { isTrackpadPinchStyleWheel });
    const isInertiaScroll = state.inertiaScrollDetector.isInertiaScroll();

    event.preventDefault();
    event.stopPropagation();

    isWheelZoomScroll(event)
        ? processImageModalZoomWheelEvent(event, state, handlers, { platformDetails }, isInertiaScroll)
        : processImageModalPanWheelEvent(event, state, handlers, isInertiaScroll);
};
